import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  empType,
  LicenseTypes,
  Operation,
  PriorityEnum,
} from "../../../../../../../utils/Enum.types";
import { Button } from "../../../../../../../stories/Button/Button";
import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid-pro";
import { useLazyQuery, useMutation } from "@apollo/client";
import { AtsSubsidiaryOrderField } from "../../queries/types";

import useUrlParams from "../../../../../../../customHooks/useUrlParams";
import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
  debouncedDelay,
  emptyMessage,
} from "../../../../../../../utils/constants";
import { OrderDirection } from "../../../../../../../utils/commonQueryTypes";
import { TableHeaderProps } from "../../../../../../../utils/types";
import { TABLE_ROW_HEIGHT } from "../../../../../../../styles/Variables";
import {
  getModifiedScrollHeight,
  handleMUISelectEvent,
} from "../../../../../../../utils/UtilFunctions";
import { StyledDatagrid } from "../../../../../../../styles/DatagridStyles";
import { AppContext } from "../../../../../../../context/context";
import { Title } from "../../../../../../../stories/Title/Title";
import useLoggedInUserDetails from "../../../../../../../customHooks/useLoggedInUserDetails";
import { useNavigate } from "react-router-dom";
import LoadingModal from "../../../../../../pages/LoadingModal";
import MessageModal from "../../../../../../../customComponents/MessageModal";
import Navbar from "../../../../../../common/layout/Navbar";
import useLabels from "../../../../../../../customHooks/useLabels";
import { GetJunctions } from "./../queries/queries";
import {
  GetJunctionsData,
  GetJunctionsVars,
  JunctionEdge,
  JunctionQueryType,
} from "./../queries/types";
import Input from "../../../../../../../stories/Input/Input";
import { Checkbox, TextField } from "@mui/material";
import useAssetTableJson from "../../../../../../../json/useAssetTableJson";
import {
  FormAutocomplete,
  LabeledAutocomplete,
  formClasses,
  labelClasses,
} from "../../../../../../../styles/AutocompleteStyles";
import { Keys } from "../../../../../../../utils/Enum.keys";
import {
  PriorityType,
  UpdateJunctionPriorityResult,
  UpdateJunctionPriorityVar,
} from "./queries/types";
import { UpdateJunctionPriority } from "./queries/mutations";
import { ColumnVisibilityFor } from "../../../../../../../utils/Enum.Datagrid";
import { useDebounce } from "../../../../../../../customHooks/useDebounce";

export interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface CustomModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  contentComponent: React.ReactNode;
}

const ViewJunctions = () => {
  const { junctionLabel } = useLabels();

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [junctions, setJunctions] = useState<JunctionEdge[]>([]);
  const [message, setMessage] = useState(emptyMessage);
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [junctionFilter, setJunctionFilter] = useState("");
  const [priority, setPriority] = useState("");

  const { customer_id, sub_id, siteID, company_id } = useUrlParams();
  const { JunctionsTable } = useAssetTableJson();
  const { state, dispatch } = useContext(AppContext);
  const { user_details } = useLoggedInUserDetails();

  const navigate = useNavigate();
  const priorityOptions = Object.values(PriorityType);

  const [getJunctions, { data, loading, fetchMore }] = useLazyQuery<
    GetJunctionsData,
    GetJunctionsVars
  >(GetJunctions);

  const [updateJunctionPriority, { loading: updateLoading }] = useMutation<
    UpdateJunctionPriorityResult,
    UpdateJunctionPriorityVar
  >(UpdateJunctionPriority, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  useEffect(() => {
    if (customer_id) {
      getJunctions({
        variables: {
          after: null,
          customerID: customer_id,
          companyID: company_id ? company_id : 0,
          first: ROWS_PER_PAGE,
          empID: user_details.id,
          priority: junctionFilter ? [junctionFilter as PriorityEnum] : [],
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsSubsidiaryOrderField.NAME,
          },
          name: debouncedSearch,
          subsidiaryID: sub_id ? sub_id : 0,
          siteID: siteID ? siteID : 0,
          ldgrID: 0,
          queryType: JunctionQueryType.ALL
        },
      });
    }
  }, [
    getJunctions,
    customer_id,
    company_id,
    sub_id,
    state.subsidiaryId,
    user_details.id,
    siteID,
    debouncedSearch,
    junctionFilter,
  ]);

  const handleCheckboxChange = (id: number) => {
    setSelectedItems(
      (prevSelected) =>
        prevSelected.includes(id)
          ? prevSelected.filter((itemId) => itemId !== id) // Remove if already selected
          : [...prevSelected, id] // Add if not selected
    );
  };

  const dynamicHeaders: TableHeaderProps[] = JunctionsTable.List.map(
    (header) => ({
      headerName: header.headerName,
      className: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (header.field === "id") {
          return (
            <>
              <Checkbox
                value={params.row}
                checked={selectedItems.includes(params.row.junctionID)}
                onChange={() => handleCheckboxChange(params.row.junctionID)}
                inputProps={{ "aria-label": "controlled" }}
              />
            </>
          );
        }

        return params.value;
      },
    })
  );
  const columns: GridColDef[] = [...dynamicHeaders];

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetJunctions.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = junctions.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setJunctions(updatedNewData);
      } else {
        setJunctions(newData);
      }
      setEndCursor(data.GetJunctions.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  const rows = useMemo(() => {
    return data
      ? data.GetJunctions.edges.map(({ node }, index) => ({
          id: index + 1,
          junction_name: node.name,
          code: node.code,
          company_addr: node.address,
          junctionID: node.id,
          priority: node.priority,
        }))
      : [];
  }, [data]);

  const handleScroll = () => {
    fetchMore({
      variables: {
        first: FETCH_MORE_DATA,
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prevResult;

        const newEdges = fetchMoreResult.GetJunctions.edges;
        const pageInfo = fetchMoreResult.GetJunctions.pageInfo;
        setEndCursor(pageInfo.endCursor);
        setHasNextPage(pageInfo.hasNextPage);

        const duplicateCheck = prevResult.GetJunctions.edges.filter(
          ({ node: { id } }) =>
            newEdges.findIndex(({ node: { id: newId } }) => newId === id) !== -1
        );

        if (duplicateCheck.length > 0) return prevResult;

        return {
          GetJunctions: {
            edges: [...junctions, ...newEdges],
            pageInfo,
            totalCount: data ? data.GetJunctions.totalCount! : 0,
          },
        };
      },
    });
  };

  const HandlePriority = async () => {
    if (priority) {
      updateJunctionPriority({
        variables: {
          user_details,
          priority: priority as PriorityType,
          junctionIds: selectedItems,
        },
        refetchQueries: [
          {
            query: GetJunctions,
            variables: {
              after: null,
              customerID: customer_id,
              companyID: company_id ? company_id : 0,
              first: ROWS_PER_PAGE,
              empID: user_details.id,
              priority: [],
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsSubsidiaryOrderField.NAME,
              },
              name: debouncedSearch,
              subsidiaryID: sub_id ? sub_id : 0,
              siteID: siteID ? siteID : 0,
            },
          },
        ],
      }).then(({ data }) => {
        if (data && data.UpdateJunctionPriority) {
          setSelectedItems([]);
          setPriority("");
          setJunctionFilter("");
          setMessage({
            flag: true,
            message: `${junctionLabel} Priority updated successfully`,
            operation: Operation.UPDATE,
          });
        }
      });
    }
  };

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(ColumnVisibilityFor.JUNCTIONS_LIST);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.JUNCTIONS_LIST,
      JSON.stringify(newModel)
    );
  };

  return (
    <>
      <Navbar
        NavType={
          state.empSubType === empType.PMU
            ? LicenseTypes.DASHBOARD
            : LicenseTypes.COMPANY
        }
      />
      <Title>{junctionLabel}</Title>
      <div className="junctions">
        <div className="junctions__select row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchData(e.target.value);
              }}
            />
          </div>
          <div className="col-2">
            <LabeledAutocomplete
              className={labelClasses.inputRoot}
              onKeyDown={(e) => {
                if (e.key === Keys.ENTER) {
                  e.preventDefault();
                  handleMUISelectEvent(e);
                }
              }}
              options={priorityOptions}
              openOnFocus
              forcePopupIcon
              freeSolo
              onChange={(e, newValue) => {
                if (newValue) {
                  setJunctionFilter(newValue as string);
                } else {
                  setJunctionFilter("");
                }
              }}
              value={junctionFilter}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  className={labelClasses.formControlRoot}
                  label="Filter by Priority"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              )}
            />
          </div>
          <div className="col-2">
            <LabeledAutocomplete
              className={labelClasses.inputRoot}
              options={priorityOptions}
              onKeyDown={(e) => {
                if (e.key === Keys.ENTER) {
                  e.preventDefault();
                  handleMUISelectEvent(e);
                }
              }}
              onChange={(e, newValue) => {
                if (newValue) {
                  setPriority(newValue as string);
                } else {
                  setPriority("");
                }
              }}
              value={priority}
              openOnFocus
              forcePopupIcon
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  className={labelClasses.formControlRoot}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  label="Move to Priority"
                />
              )}
            />
          </div>
        </div>
        <div className="junctions__tableblock">
          <StyledDatagrid
            columns={
              state.empSubType === empType.SYS_ADMIN ? columns : dynamicHeaders
            }
            rows={rows}
            rowHeight={TABLE_ROW_HEIGHT}
            disableRowSelectionOnClick
            hideFooter
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            initialState={{
              columns: {
                columnVisibilityModel: columnVisibilityModel,
              },
            }}
            onRowsScrollEnd={() => handleScroll()}
          />
        </div>

        <div className="junctions__button--group">
          <div className="col">
            <Button onClick={HandlePriority} mode="save" />
            <Button
              mode="back"
              onClick={() => {
                navigate(-1);
              }}
            />
          </div>
          <div className="overall-total-count">
            Total count : <b>{data?.GetJunctions.totalCount}</b>
          </div>
        </div>
      </div>
      <MessageModal
        handleClose={() => setMessage(emptyMessage)}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal flag={(!searchData && loading) || updateLoading} />
    </>
  );
};

export default ViewJunctions;
